import { clearTokens, getAccess, getRefresh, setTokens } from './auth';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface ErrorBody {
  code?: string;
  message?: string;
  error?: string;
  details?: unknown;
}

/**
 * Exported because the agent's streaming turn cannot go through `api()`.
 * `api()` reads the whole body as JSON before it returns, which is the one
 * thing a stream must not do — so useAgentChat issues a raw fetch and needs the
 * same base URL rather than a second, silently diverging copy of it.
 */
export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/erp';

/**
 * Single in-flight refresh promise so N parallel 401s only trigger ONE refresh.
 * Resolves to the new access token, or null if refresh failed.
 */
let refreshing: Promise<string | null> | null = null;

/**
 * A usable access token, refreshing first if the current one has expired.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS EXPORTED
 *
 * `api()` handles a 401 by refreshing and retrying, and every JSON call goes
 * through it. The agent's streaming call does not and cannot — it reads an
 * NDJSON body incrementally while `api()` parses the whole response as JSON
 * before returning — so it was written as a raw fetch with whatever token
 * happened to be in storage.
 *
 * Access tokens live fifteen minutes. Anybody working for longer than that,
 * which is everybody, eventually sent a message on a dead token: the stream
 * 401'd or was cut mid-body (ERR_INCOMPLETE_CHUNKED_ENCODING), and the panel
 * showed "Something went wrong. Try again." Trying again failed the same way,
 * because nothing about the expiry had changed.
 *
 * The refresh machinery was there the whole time. It simply was not reachable
 * from the one call that needed it most.
 */
export async function freshAccessToken(): Promise<string | null> {
  const token = getAccess();
  if (token && !isExpired(token)) return token;
  return tryRefresh();
}

/**
 * Is this JWT past its expiry? Read locally, with a small margin.
 *
 * The margin matters for a stream: a token with four seconds left passes a
 * naive check, and then dies in the middle of the answer — which is the worst
 * moment, because the customer has already watched half of it arrive.
 */
function isExpired(token: string, marginSeconds = 60): boolean {
  try {
    const [, payload] = token.split('.');
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    if (!exp) return false;
    return Date.now() / 1000 >= exp - marginSeconds;
  } catch {
    return false; // unreadable — let the server be the judge
  }
}

async function tryRefresh(): Promise<string | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const attempt = async (token: string): Promise<string | null> => {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: token }),
      });
      if (!res.ok) return null;
      const j = (await res.json()) as { access?: string; refresh?: string | null };
      if (!j.access) return null;
      setTokens(j.access, j.refresh ?? null);
      return j.access;
    };
    const refresh = getRefresh();
    if (!refresh) return null;
    try {
      const first = await attempt(refresh);
      if (first) return first;
      // Another tab may have rotated the shared refresh token between our read
      // and this call. Re-read once; if it changed, retry with the fresh token
      // so a benign concurrent rotation doesn't force a spurious logout.
      const latest = getRefresh();
      if (latest && latest !== refresh) {
        return await attempt(latest);
      }
      return null;
    } catch {
      return null;
    }
  })();
  try {
    return await refreshing;
  } finally {
    refreshing = null;
  }
}

function fullUrl(path: string): string {
  return path.startsWith('http') || path.startsWith('/api')
    ? path
    : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

async function attemptLogout(): Promise<void> {
  clearTokens();
  if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/erp/login')) {
    window.location.replace('/erp/login');
  }
}

export async function api<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const url = fullUrl(path);
  const headers = new Headers(init.headers);

  const access = getAccess();
  if (access && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${access}`);
  }

  let body = init.body;
  if (body !== undefined && body !== null && !(body instanceof FormData) && typeof body === 'object') {
    body = JSON.stringify(body);
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }

  let response: Response;
  try {
    response = await fetch(url, { ...init, headers, body });
  } catch (cause) {
    throw new ApiError(0, 'NETWORK_ERROR', 'Network request failed', cause);
  }

  if (response.status === 401) {
    // Try to refresh exactly once before giving up. If the refresh succeeds,
    // re-issue the original request transparently — caller never sees a 401.
    if (!retried) {
      const newAccess = await tryRefresh();
      if (newAccess) {
        return api<T>(path, init, true);
      }
    }
    await attemptLogout();
    throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired');
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    let payload: ErrorBody = {};
    if (isJson) {
      try {
        payload = (await response.json()) as ErrorBody;
      } catch {
        payload = {};
      }
    } else {
      try {
        payload = { message: await response.text() };
      } catch {
        payload = {};
      }
    }
    throw new ApiError(
      response.status,
      payload.code ?? `HTTP_${response.status}`,
      payload.message ?? payload.error ?? response.statusText ?? 'Request failed',
      payload.details,
    );
  }

  if (response.status === 204) return undefined as T;
  if (isJson) return (await response.json()) as T;
  return (await response.text()) as unknown as T;
}

// Convenience helpers — accept JSON body shorthand.
api.get = <T>(path: string): Promise<T> => api<T>(path);
api.post = <T>(path: string, body?: unknown): Promise<T> =>
  api<T>(path, { method: 'POST', body: body as BodyInit | undefined });
api.patch = <T>(path: string, body?: unknown): Promise<T> =>
  api<T>(path, { method: 'PATCH', body: body as BodyInit | undefined });
api.delete = <T>(path: string): Promise<T> => api<T>(path, { method: 'DELETE' });

/**
 * POSTs to a PDF endpoint, reads the binary blob, and opens it in a new tab.
 * Falls back to a download anchor if popups are blocked.
 */
export async function downloadPdf(
  path: string,
  filename: string,
  body?: Record<string, unknown>,
  retried = false,
): Promise<void> {
  const url = fullUrl(path);
  const headers = new Headers();
  const access = getAccess();
  if (access) headers.set('Authorization', `Bearer ${access}`);

  let payloadBody: BodyInit | undefined;
  if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
    payloadBody = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, { method: 'POST', headers, body: payloadBody });
  } catch (cause) {
    throw new ApiError(0, 'NETWORK_ERROR', 'Network request failed', cause);
  }

  if (response.status === 401) {
    if (!retried) {
      const newAccess = await tryRefresh();
      if (newAccess) return downloadPdf(path, filename, body, true);
    }
    await attemptLogout();
    throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired');
  }

  if (!response.ok) {
    let payload: ErrorBody = {};
    try {
      payload = (await response.json()) as ErrorBody;
    } catch {
      payload = {};
    }
    throw new ApiError(
      response.status,
      payload.code ?? `HTTP_${response.status}`,
      payload.message ?? payload.error ?? response.statusText ?? 'Request failed',
      payload.details,
    );
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const win = typeof window !== 'undefined' ? window.open(objectUrl, '_blank', 'noopener') : null;
  if (!win && typeof document !== 'undefined') {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  if (typeof window !== 'undefined') {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  }
}

/**
 * POSTs to a PDF endpoint, returns a temporary object URL for inline preview
 * (e.g. inside an <iframe>). Caller must revoke when done.
 */
export async function fetchPdfBlobUrl(
  path: string,
  body?: Record<string, unknown>,
  retried = false,
): Promise<string> {
  const url = fullUrl(path);
  const headers = new Headers();
  const access = getAccess();
  if (access) headers.set('Authorization', `Bearer ${access}`);

  let payloadBody: BodyInit | undefined;
  if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
    payloadBody = JSON.stringify(body);
  }

  const response = await fetch(url, { method: 'POST', headers, body: payloadBody });

  if (response.status === 401) {
    if (!retried) {
      const newAccess = await tryRefresh();
      if (newAccess) return fetchPdfBlobUrl(path, body, true);
    }
    await attemptLogout();
    throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired');
  }
  if (!response.ok) {
    let p: ErrorBody = {};
    try { p = (await response.json()) as ErrorBody; } catch { /* ignore */ }
    throw new ApiError(response.status, p.code ?? `HTTP_${response.status}`, p.message ?? p.error ?? 'Request failed');
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}
